import React, { useState, useMemo, useCallback } from 'react';
import { Zap, ChevronUp, ChevronDown, Filter, ChevronRight } from 'lucide-react';
import { TiltViewDetailFilters } from './ui/TiltViewDetailFilters';
import { Button } from './ui/button';
import { getStatusDisplay, statusFilterDisplayOptions } from '../lib/utils';
import { formatTimestamp, formatTimeOnly, formatDateTime } from '../lib/formatUtils';
import { convertWeldDataValue, getParameterUnitSuffix, getUnitFromSettings, formatTimestampForDisplay } from '../utils/unitUtils';
import { extractM500ZoneDetailsFromData } from '../utils/m500ZoneUtils';
import { getTiltStatus, getStatusColorClass } from '../utils/statusUtils';

// Add custom scrollbar styles
const scrollbarStyles = `
  .scrollbar-thin::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: #9ca3af #f1f1f1;
  }

  /* Smooth scrolling */
  .scrollbar-thin {
    scroll-behavior: smooth;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    .mobile-scroll-container {
      -webkit-overflow-scrolling: touch;
      overscroll-behavior: contain;
    }
  }
`;

// Define parameter limits for color coding (in inches - will be converted based on display unit)
const PARAMETER_LIMITS = {
  voltage: { min: 20, max: 30 },
  current: { min: 150, max: 250 },
  travelSpeed: { min: 100, max: 300 },
  heat: { min: 1.0, max: 2.0 },
  trueEnergy: { min: 2.5, max: 4.5 },
  oscillationWidth: { min: 0.01, max: 10.0 },
  wireSpeed: { min: 150, max: 450 },
  totalWireConsumed: { min: 0, max: 1000 },
  distance: { min: 0, max: 1000 }
};

// Get converted parameter limits based on display unit
const getConvertedParameterLimits = () => {
  const displayUnit = getUnitFromSettings();
  
  if (displayUnit === 'Inches') {
    return PARAMETER_LIMITS;
  }
  
  // Convert limits from inches to mm for display
  return {
    voltage: PARAMETER_LIMITS.voltage,
    current: PARAMETER_LIMITS.current,
    travelSpeed: { 
      min: PARAMETER_LIMITS.travelSpeed.min * 25.4, 
      max: PARAMETER_LIMITS.travelSpeed.max * 25.4 
    },
    heat: PARAMETER_LIMITS.heat,
    trueEnergy: PARAMETER_LIMITS.trueEnergy,
    oscillationWidth: { 
      min: PARAMETER_LIMITS.oscillationWidth.min * 25.4, 
      max: PARAMETER_LIMITS.oscillationWidth.max * 25.4 
    },
    wireSpeed: { 
      min: PARAMETER_LIMITS.wireSpeed.min * 25.4, 
      max: PARAMETER_LIMITS.wireSpeed.max * 25.4 
    },
    totalWireConsumed: { 
      min: PARAMETER_LIMITS.totalWireConsumed.min * 25.4, 
      max: PARAMETER_LIMITS.totalWireConsumed.max * 25.4 
    },
    distance: { 
      min: PARAMETER_LIMITS.distance.min * 25.4, 
      max: PARAMETER_LIMITS.distance.max * 25.4 
    }
  };
};

// Helper function to render converted min/max values for headers (only for distance-based parameters)
const renderHeaderLimits = (paramName: keyof typeof PARAMETER_LIMITS) => {
  const limits = getConvertedParameterLimits()[paramName];
  const minValue = limits.min.toFixed(0);
  const maxValue = limits.max.toFixed(0);
  const avgValue = ((limits.min + limits.max) / 2).toFixed(0);
  
  return (
    <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
      <div>Min: {minValue}</div>
      <div>Avg: {avgValue}</div>
      <div>Max: {maxValue}</div>
    </div>
  );
};

// Helper function to render static min/avg/max labels for non-distance parameters
const renderStaticHeaderLabels = () => {
  return (
    <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
      <div>Min</div>
      <div>Avg</div>
      <div>Max</div>
    </div>
  );
};


// Sorting types
interface Sorting {
  field: string;
  order: 'ascend' | 'descend';
}

interface TiltViewDetailTableProps {
  detailData: any[];
  onRowSelect?: (index: number | null) => void;
  selectedRowIndex?: number | null;
  loading?: boolean;
  tiltStart?: number;
  tiltEnd?: number;
  statusCalculationMethod?: string;
  visibleColumns?: string[];
}

const TiltViewDetailTable: React.FC<TiltViewDetailTableProps> = ({
  detailData,
  onRowSelect,
  selectedRowIndex,
  loading = false,
  tiltStart,
  tiltEnd,
  statusCalculationMethod = 'Instantaneous',
  visibleColumns
}) => {
  // Helper function to check if a column is visible
  const isColumnVisible = useCallback((columnKey: string): boolean => {
    if (!visibleColumns || visibleColumns.length === 0) {
      return true; // Show all columns if no visibility specified
    }
    return visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  // Helper function to calculate sticky left position for a column
  const getStickyLeft = useCallback((columnKey: string): string => {
    const stickyColumnOrder = ['sl-no', 'status', 'event', 'time'];
    const stickyColumnWidths: Record<string, number> = {
      'sl-no': 80,
      'status': 60,
      'event': 140,
      'time': 180
    };
    
    let left = 0;
    const currentIndex = stickyColumnOrder.indexOf(columnKey);
    for (let i = 0; i < currentIndex; i++) {
      if (isColumnVisible(stickyColumnOrder[i])) {
        left += stickyColumnWidths[stickyColumnOrder[i]];
      }
    }
    return `${left}px`;
  }, [isColumnVisible]);

  // Sorting state
  const [sorting, setSorting] = useState<Sorting | null>(null);
  
  // Filter card state
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  // Helper function to check if a parameter is within limits
  const isWithinLimits = (value: number, paramName: keyof typeof PARAMETER_LIMITS): boolean => {
    const limits = getConvertedParameterLimits()[paramName];
    return value >= limits.min && value <= limits.max;
  };

  // Helper function to render parameter value with color coding
  const renderParameterValue = (value: number | null, paramName: keyof typeof PARAMETER_LIMITS, decimals: number = 2, status?: boolean) => {
    if (value === null || value === undefined || isNaN(value)) {
      return <span className="text-gray-400">-</span>;
    }
    
    // Convert all parameters that need conversion (all except voltage, current, impedance)
    let displayValue;
    if (['travelSpeed', 'wireSpeed', 'heat', 'trueEnergy', 'oscillationWidth', 'totalWireConsumed', 'distance'].includes(paramName)) {
      // Convert value from inches to display unit for distance-based parameters
      const convertedValue = convertWeldDataValue(value, paramName);
      displayValue = convertedValue.toFixed(decimals);
    } else {
      // Keep original value for voltage, current, impedance, etc.
      displayValue = value.toFixed(decimals);
    }
    
    // Apply red background if status is false, otherwise keep normal styling
    const bgColor = status === false ? 'bg-red-100' : 'bg-white';
    const textColor = status === false ? 'text-red-800' : 'text-gray-600';
    
    return (
      <span className={`${textColor} ${bgColor} px-2 py-1 rounded`}>
        {displayValue}
      </span>
    );
  };

  // Filter states
  const [eventFilter, setEventFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [timeFilter, setTimeFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [tiltFilter, setTiltFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [passFilter, setPassFilter] = useState<string[]>([]);
  const [zoneFilter, setZoneFilter] = useState<string[]>([]);
  const [voltageFilter, setVoltageFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [currentFilter, setCurrentFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [travelSpeedFilter, setTravelSpeedFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [wireSpeedFilter, setWireSpeedFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [heatFilter, setHeatFilter] = useState<{ min: string; max: string }>({ min: '', max: '' });
  const [dateRange, setDateRange] = useState<{ dateFrom: Date | undefined; dateTo: Date | undefined }>({
    dateFrom: undefined,
    dateTo: undefined
  });



  // Sorting function
  const handleSort = (field: string) => {
    if (sorting?.field === field) {
      setSorting(sorting.order === 'ascend' ? { field, order: 'descend' } : null);
    } else {
      setSorting({ field, order: 'ascend' });
    }
  };

  // Render sort icon
  const renderSortIcon = (field: string) => {
    if (sorting?.field !== field) {
      return <ChevronUp className="h-3 w-3 text-gray-400" />;
    }
    return sorting.order === 'ascend' ? 
      <ChevronUp className="h-3 w-3 text-blue-600" /> : 
      <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  // Get dynamic unit labels for table headers
  const getUnitLabel = (parameter: string): string => {
    return getParameterUnitSuffix(parameter);
  };

  // Process data with M500 zone details
  const processedData = useMemo(() => {
    return detailData.map((row: any) => {
      // Check if it's an M500 system
      const isM500System = row?.welding_system?.toLowerCase() === "m500";
      
      // Extract M500 zone details if it's an M500 system
      let m500ZoneData = {};
      if (isM500System) {
        m500ZoneData = extractM500ZoneDetailsFromData(row);
      }

      return {
        ...row,
        m500ZoneDetails: m500ZoneData,
        isM500System
      };
    });
  }, [detailData]);

  // Check if we have any M500 systems in the data
  const hasM500Data = useMemo(() => {
    return processedData.some((row: any) => row.isM500System === true);
  }, [processedData]);

  // Apply TLOG filtering based on tilt_start and tilt_end parameters
  const tiltFilteredData = useMemo(() => {
    // Check if we have tilt parameters for filtering
    if (tiltStart !== undefined && tiltEnd !== undefined) {
      return processedData.filter((row: any) => {
        const rowTilt = parseFloat(row.tilt) || 0;
        // Special case: tilt_start is passed even when it's 0 (not just truthy values)
        return rowTilt >= tiltStart && rowTilt <= tiltEnd;
      });
    }
    return processedData;
  }, [processedData, tiltStart, tiltEnd]);

  // Get unique options for dropdowns
  const uniqueOptions = useMemo(() => {
    const options = {
      events: Array.from(new Set(tiltFilteredData.map(row => row.event).filter(Boolean))),
      statuses: statusFilterDisplayOptions.map(option => option.value),
      passes: Array.from(new Set(tiltFilteredData.map(row => row.pass || row.pass_name).filter(Boolean))),
      zones: Array.from(new Set(tiltFilteredData.map(row => row.zone || row.zone_name).filter(Boolean)))
    };
    return options;
  }, [tiltFilteredData]);

  // Apply filters to data
  const filteredData = useMemo(() => {
    return tiltFilteredData.filter((row: any) => {
      // Event filter
      if (eventFilter.length > 0 && row.event && !eventFilter.includes(row.event)) {
        return false;
      }

      // Status filter
      if (statusFilter.length > 0) {
        const status = getTiltStatus(statusCalculationMethod, row);
        if (status && !statusFilter.includes(status)) {
          return false;
        }
      }

      // Time filter (range)
      if (timeFilter.min || timeFilter.max) {
        const rowTime = row.time;
        if (rowTime !== null && rowTime !== undefined) {
          const timeValue = Number(rowTime);
          if (timeFilter.min && timeValue < Number(timeFilter.min)) return false;
          if (timeFilter.max && timeValue > Number(timeFilter.max)) return false;
        }
      }

      // Tilt filter (range)
      if (tiltFilter.min || tiltFilter.max) {
        const rowTilt = row.tilt;
        if (rowTilt !== null && rowTilt !== undefined) {
          const tiltValue = Number(rowTilt);
          if (tiltFilter.min && tiltValue < Number(tiltFilter.min)) return false;
          if (tiltFilter.max && tiltValue > Number(tiltFilter.max)) return false;
        }
      }

      // Pass filter
      if (passFilter.length > 0) {
        const rowPass = row.pass || row.pass_name;
        if (!rowPass || !passFilter.includes(rowPass)) {
          return false;
        }
      }

      // Zone filter
      if (zoneFilter.length > 0) {
        const rowZone = row.zone || row.zone_name;
        if (!rowZone || !zoneFilter.includes(rowZone)) {
          return false;
        }
      }

      // Voltage filter (range)
      if (voltageFilter.min || voltageFilter.max) {
        const rowVoltage = row.volts;
        if (rowVoltage !== null && rowVoltage !== undefined) {
          const voltageValue = Number(rowVoltage);
          if (voltageFilter.min && voltageValue < Number(voltageFilter.min)) return false;
          if (voltageFilter.max && voltageValue > Number(voltageFilter.max)) return false;
        }
      }

      // Current filter (range)
      if (currentFilter.min || currentFilter.max) {
        const rowCurrent = row.current;
        if (rowCurrent !== null && rowCurrent !== undefined) {
          const currentValue = Number(rowCurrent);
          if (currentFilter.min && currentValue < Number(currentFilter.min)) return false;
          if (currentFilter.max && currentValue > Number(currentFilter.max)) return false;
        }
      }

      // Travel speed filter (range)
      if (travelSpeedFilter.min || travelSpeedFilter.max) {
        const rowTravelSpeed = row.travel_speed;
        if (rowTravelSpeed !== null && rowTravelSpeed !== undefined) {
          const travelSpeedValue = Number(rowTravelSpeed);
          if (travelSpeedFilter.min && travelSpeedValue < Number(travelSpeedFilter.min)) return false;
          if (travelSpeedFilter.max && travelSpeedValue > Number(travelSpeedFilter.max)) return false;
        }
      }

      // Wire speed filter (range)
      if (wireSpeedFilter.min || wireSpeedFilter.max) {
        const rowWireSpeed = row.wire_speed;
        if (rowWireSpeed !== null && rowWireSpeed !== undefined) {
          const wireSpeedValue = Number(rowWireSpeed);
          if (wireSpeedFilter.min && wireSpeedValue < Number(wireSpeedFilter.min)) return false;
          if (wireSpeedFilter.max && wireSpeedValue > Number(wireSpeedFilter.max)) return false;
        }
      }

      // Heat filter (range)
      if (heatFilter.min || heatFilter.max) {
        const rowHeat = row.heat;
        if (rowHeat !== null && rowHeat !== undefined) {
          const heatValue = Number(rowHeat);
          if (heatFilter.min && heatValue < Number(heatFilter.min)) return false;
          if (heatFilter.max && heatValue > Number(heatFilter.max)) return false;
        }
      }

      // Date range filter
      if (dateRange.dateFrom || dateRange.dateTo) {
        const rowTime = row.time;
        if (rowTime) {
          const rowDate = new Date(rowTime * 1000);
          const fromDate = dateRange.dateFrom ? new Date(dateRange.dateFrom) : null;
          const toDate = dateRange.dateTo ? new Date(dateRange.dateTo) : null;
          
          if (fromDate && rowDate < fromDate) return false;
          if (toDate && rowDate > toDate) return false;
        }
      }

      return true;
    });
  }, [detailData, eventFilter, statusFilter, timeFilter, tiltFilter, passFilter, zoneFilter, voltageFilter, currentFilter, travelSpeedFilter, wireSpeedFilter, heatFilter, dateRange]);

  // Apply sorting to filtered data
  const sortedData = useMemo(() => {
    if (!sorting) return filteredData;

    return [...filteredData].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sorting.field) {
        case 'event':
          aValue = a.event || '';
          bValue = b.event || '';
          break;
        case 'status':
          // Use instantaneous_status if available, otherwise fall back to calculated status
          const aInstStatus = a.instantaneous_status;
          const bInstStatus = b.instantaneous_status;
          
          if (aInstStatus !== undefined && aInstStatus !== null) {
            aValue = aInstStatus;
          } else {
            aValue = getTiltStatus(statusCalculationMethod, a) || '';
          }
          
          if (bInstStatus !== undefined && bInstStatus !== null) {
            bValue = bInstStatus;
          } else {
            bValue = getTiltStatus(statusCalculationMethod, b) || '';
          }
          break;
        case 'time':
          aValue = a.time || '';
          bValue = b.time || '';
          break;
        case 'tilt':
          aValue = a.tilt || 0;
          bValue = b.tilt || 0;
          break;
        case 'pass':
          aValue = a.pass || a.pass_name || '';
          bValue = b.pass || b.pass_name || '';
          break;
        case 'pipe':
          aValue = a.pipe_name || '';
          bValue = b.pipe_name || '';
          break;
        case 'zone':
          aValue = a.zone || a.zone_name || '';
          bValue = b.zone || b.zone_name || '';
          break;
        case 'voltage':
          aValue = a.volts || 0;
          bValue = b.volts || 0;
          break;
        case 'current':
          aValue = a.current || 0;
          bValue = b.current || 0;
          break;
        case 'travelSpeed':
          aValue = a.travel_speed || 0;
          bValue = b.travel_speed || 0;
          break;
        case 'wireSpeed':
          aValue = a.wire_speed || 0;
          bValue = b.wire_speed || 0;
          break;
        case 'heat':
          aValue = a.heat || 0;
          bValue = b.heat || 0;
          break;
        case 'trueEnergy':
          aValue = a.true_energy || 0;
          bValue = b.true_energy || 0;
          break;
        case 'oscillationWidth':
          aValue = a.oscillation_width || 0;
          bValue = b.oscillation_width || 0;
          break;
        case 'target':
          aValue = a.target || 0;
          bValue = b.target || 0;
          break;
        case 'horizontalBias':
          aValue = a.horizontal_bias || 0;
          bValue = b.horizontal_bias || 0;
          break;
        case 'frequency':
          aValue = a.frequency || 0;
          bValue = b.frequency || 0;
          break;
        case 'totalWireConsumed':
          aValue = a.total_wire_consumed || 0;
          bValue = b.total_wire_consumed || 0;
          break;
        case 'arcImpedance':
          aValue = a.arc_impedance || 0;
          bValue = b.arc_impedance || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sorting.order === 'ascend' ? -1 : 1;
      if (aValue > bValue) return sorting.order === 'ascend' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sorting]);

  // Calculate min/avg/max values for parameters
  const parameterStats = useMemo(() => {
    if (!detailData.length) return {};

    const stats: any = {};
    
    // Helper function to calculate stats for a field with unit conversion
    const calculateStats = (field: string, decimals: number = 2, convertParamName?: string) => {
      const values = detailData
        .map(row => {
          const val = row[field];
          if (val === null || val === undefined || isNaN(val)) return null;
          
          let numVal = Number(val);
          
          // Apply unit conversion if needed (same logic as view pages)
          if (convertParamName && ['travelSpeed', 'wireSpeed', 'heat', 'trueEnergy', 'oscillationWidth', 'totalWireConsumed', 'distance'].includes(convertParamName)) {
            numVal = convertWeldDataValue(numVal, convertParamName);
          }
          
          return numVal;
        })
        .filter((val): val is number => val !== null);

      if (values.length === 0) return { min: '-', avg: '-', max: '-' };

      const min = Math.min(...values);
      const max = Math.max(...values);
      const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

      return {
        min: min.toFixed(decimals),
        avg: avg.toFixed(decimals),
        max: max.toFixed(decimals)
      };
    };

    // Calculate stats for each parameter with unit conversion
    stats.volts = calculateStats('volts', 1);
    stats.current = calculateStats('current', 2);
    stats.travel_speed = calculateStats('travel_speed', 1, 'travelSpeed');
    stats.wire_speed = calculateStats('wire_speed', 0, 'wireSpeed');
    stats.heat = calculateStats('heat', 2, 'heat');
    stats.true_energy = calculateStats('true_energy', 1, 'trueEnergy');
    stats.oscillation_width = calculateStats('oscillation_width', 2, 'oscillationWidth');
    stats.target = calculateStats('target', 0);
    stats.horizontal_bias = calculateStats('horizontal_bias', 2);
    stats.frequency = calculateStats('frequency', 0);
    stats.total_wire_consumed = calculateStats('total_wire_consumed', 2, 'totalWireConsumed');
    stats.arc_impedance = calculateStats('arc_impedance', 0);
    stats.distance = calculateStats('distance', 2, 'distance');

    return stats;
  }, [detailData]);

  // Helper function to render actual min/avg/max values (conversion already applied in parameterStats)
  const renderActualHeaderValues = (paramName: string) => {
    const stats = parameterStats[paramName];
    if (!stats || stats.min === '-' || stats.avg === '-' || stats.max === '-') {
      return (
        <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
          <div>Min: -</div>
          <div>Avg: -</div>
          <div>Max: -</div>
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-3 text-xs font-normal text-center text-gray-500">
        <div>Min: {stats.min}</div>
        <div>Avg: {stats.avg}</div>
        <div>Max: {stats.max}</div>
      </div>
    );
  };

  // Decimal places for different parameters
  const decimalPlaces = {
    volts: 1,
    current: 2,
    travel_speed: 1,
    wire_speed: 0,
    heat: 2,
    true_energy: 1,
    oscillation_width: 2
  };

  return (
    <div className="h-full flex flex-col">
      {/* Inject custom scrollbar styles */}
      <style>{scrollbarStyles}</style>

      {/* Collapsible Filters Section */}
      <div className="bg-white rounded-lg border border-gray-200 mb-4">
        <div 
          className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-50 transition-colors"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="font-medium text-gray-700">Tilt View Detail Filters</span>
          </div>
          {showFilters ? (
            <ChevronUp className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          )}
        </div>
        
        {showFilters && (
          <div className="px-4 pb-4">
            <TiltViewDetailFilters
              eventFilter={eventFilter}
              onEventFilterChange={setEventFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              timeFilter={timeFilter}
              onTimeFilterChange={setTimeFilter}
              tiltFilter={tiltFilter}
              onTiltFilterChange={setTiltFilter}
              passFilter={passFilter}
              onPassFilterChange={setPassFilter}
              zoneFilter={zoneFilter}
              onZoneFilterChange={setZoneFilter}
              voltageFilter={voltageFilter}
              onVoltageFilterChange={setVoltageFilter}
              currentFilter={currentFilter}
              onCurrentFilterChange={setCurrentFilter}
              travelSpeedFilter={travelSpeedFilter}
              onTravelSpeedFilterChange={setTravelSpeedFilter}
              wireSpeedFilter={wireSpeedFilter}
              onWireSpeedFilterChange={setWireSpeedFilter}
              heatFilter={heatFilter}
              onHeatFilterChange={setHeatFilter}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              eventOptions={uniqueOptions.events}
              passOptions={uniqueOptions.passes}
              zoneOptions={uniqueOptions.zones}
              totalCount={detailData.length}
              filteredCount={filteredData.length}
            />
          </div>
        )}
      </div>

      {/* Table Content - Single unified table with sticky columns */}
      <div 
        className="flex-1 overflow-x-auto overflow-y-auto pb-20 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100"
        style={{
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain'
        }}
      >
        {/* Single unified table with sticky columns */}
        <table className="border-collapse w-full" style={{ tableLayout: 'auto', minWidth: 'max-content' }}>
          <thead className="sticky top-0 bg-white z-30">
                <tr className="border-b h-[72px] bg-gray-50">
                  {/* Sticky Sl.no column */}
                  {isColumnVisible('sl-no') && (
                    <th className={`sticky ${getStickyLeft('sl-no')} z-20 bg-gray-50 w-[80px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>
                      <div className="flex items-center justify-center gap-1">
                        <span className="text-xs">Sl.no</span>
                      </div>
                    </th>
                  )}
                  {/* Sticky Status column (conditionally rendered) */}
                  {statusCalculationMethod === 'Instantaneous' && isColumnVisible('status') && (
                    <th className={`sticky ${getStickyLeft('status')} z-20 bg-gray-50 w-[60px] px-2 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>
                      <button
                        onClick={() => handleSort('status')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Status</span>
                        {renderSortIcon('status')}
                      </button>
                    </th>
                  )}
                  {/* Sticky Event column */}
                  {isColumnVisible('event') && (
                    <th className={`sticky ${getStickyLeft('event')} z-20 bg-gray-50 w-[140px] px-4 text-center text-xs font-medium text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>
                      <button
                        onClick={() => handleSort('event')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Event</span>
                        {renderSortIcon('event')}
                      </button>
                    </th>
                  )}
                  {/* Sticky Time column */}
                  {isColumnVisible('time') && (
                    <th className={`sticky ${getStickyLeft('time')} z-20 bg-gray-50 w-[180px] px-4 text-center text-xs font-medium text-gray-600`} style={{ boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>
                      <button
                        onClick={() => handleSort('time')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Time</span>
                        {renderSortIcon('time')}
                      </button>
                    </th>
                  )}
                  {/* Scrollable columns start here */}
                  {isColumnVisible('tilt') && (
                    <th className="w-[100px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('tilt')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Tilt</span>
                        {renderSortIcon('tilt')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('pass') && (
                    <th className="w-[120px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('pass')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Pass</span>
                        {renderSortIcon('pass')}
                      </button>
                    </th>
                  )}
                  {hasM500Data && isColumnVisible('pipe') && (
                    <th className="w-[120px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('pipe')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Pipe</span>
                        {renderSortIcon('pipe')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('zone') && (
                    <th className="w-[120px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('zone')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Zone</span>
                        {renderSortIcon('zone')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('distance') && (
                    <th className="w-[120px] px-4 text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('distance')}
                        className="flex items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <span className="text-xs">Distance (in)</span>
                        {renderSortIcon('distance')}
                      </button>
                    </th>
                  )}
                  {/* Parameter columns - all in same header row */}
                  {isColumnVisible('travel-speed') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600">
                      <button
                        onClick={() => handleSort('travelSpeed')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Travel Speed ({getUnitLabel('travel_speed')})</span>
                          {renderSortIcon('travelSpeed')}
                        </div>
                        {renderActualHeaderValues('travel_speed')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('voltage') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('voltage')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          <span className="text-xs">Voltage ({getUnitLabel('volts')})</span>
                          {renderSortIcon('voltage')}
                        </div>
                        {renderActualHeaderValues('volts')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('current') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('current')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          <span className="text-xs">Current ({getUnitLabel('current')})</span>
                          {renderSortIcon('current')}
                        </div>
                        {renderActualHeaderValues('current')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('wire-speed') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('wireSpeed')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Wire Speed ({getUnitLabel('wire_speed')})</span>
                          {renderSortIcon('wireSpeed')}
                        </div>
                        {renderActualHeaderValues('wire_speed')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('oscillation-width') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('oscillationWidth')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Oscillation Width ({getUnitLabel('oscillation_width')})</span>
                          {renderSortIcon('oscillationWidth')}
                        </div>
                        {renderActualHeaderValues('oscillation_width')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('target') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('target')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Target ({getUnitLabel('target')})</span>
                          {renderSortIcon('target')}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          Min: {parameterStats.target?.min || '-'} Avg: {parameterStats.target?.avg || '-'} Max: {parameterStats.target?.max || '-'}
                        </div>
                      </button>
                    </th>
                  )}
                  {isColumnVisible('horizontal-bias') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('horizontalBias')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Horizontal Bias ({getUnitLabel('horizontal_bias')})</span>
                          {renderSortIcon('horizontalBias')}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          Min: {parameterStats.horizontal_bias?.min || '-'} Avg: {parameterStats.horizontal_bias?.avg || '-'} Max: {parameterStats.horizontal_bias?.max || '-'}
                        </div>
                      </button>
                    </th>
                  )}
                  {isColumnVisible('frequency') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('frequency')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Frequency ({getUnitLabel('frequency')})</span>
                          {renderSortIcon('frequency')}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          Min: {parameterStats.frequency?.min || '-'} Avg: {parameterStats.frequency?.avg || '-'} Max: {parameterStats.frequency?.max || '-'}
                        </div>
                      </button>
                    </th>
                  )}
                  {isColumnVisible('total-wire-consumed') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('totalWireConsumed')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Total Wire Consumed ({getUnitLabel('total_wire_consumed')})</span>
                          {renderSortIcon('totalWireConsumed')}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          Min: {parameterStats.total_wire_consumed?.min || '-'} Avg: {parameterStats.total_wire_consumed?.avg || '-'} Max: {parameterStats.total_wire_consumed?.max || '-'}
                        </div>
                      </button>
                    </th>
                  )}
                  {isColumnVisible('true-energy') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('trueEnergy')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">True Energy ({getUnitLabel('true_energy')})</span>
                          {renderSortIcon('trueEnergy')}
                        </div>
                        {renderActualHeaderValues('true_energy')}
                      </button>
                    </th>
                  )}
                  {isColumnVisible('heat') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('heat')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Heat ({getUnitLabel('heat')})</span>
                          {renderSortIcon('heat')}
                        </div>
                        {renderActualHeaderValues('heat')}
                      </button>
                    </th>
                  )}
                  {hasM500Data && isColumnVisible('head-angle') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('headAngle')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <span className="text-xs">Head Angle ({getUnitLabel('head_angle')})</span>
                          {renderSortIcon('headAngle')}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          Min: {parameterStats.head_angle?.min || '-'} Avg: {parameterStats.head_angle?.avg || '-'} Max: {parameterStats.head_angle?.max || '-'}
                        </div>
                      </button>
                    </th>
                  )}
                  {hasM500Data && isColumnVisible('arc-impedance') && (
                    <th className="px-4 min-w-[300px] text-center text-xs font-medium text-gray-600 border-l">
                      <button
                        onClick={() => handleSort('arcImpedance')}
                        className="flex flex-col items-center justify-center gap-1 hover:text-gray-800 transition-colors w-full"
                      >
                        <div className="flex items-center gap-1">
                          <Zap className="h-3 w-3" />
                          <span className="text-xs">Arc Impedance ({getUnitLabel('arc_impedance')})</span>
                          {renderSortIcon('arcImpedance')}
                        </div>
                        <div className="text-xs text-gray-500 font-normal">
                          Min: {parameterStats.arc_impedance?.min || '-'} Avg: {parameterStats.arc_impedance?.avg || '-'} Max: {parameterStats.arc_impedance?.max || '-'}
                        </div>
                      </button>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={visibleColumns ? visibleColumns.length : (hasM500Data ? 21 : 20)} className="px-4 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
                        <span className="text-gray-600 text-lg">Loading data...</span>
                      </div>
                    </td>
                  </tr>
                ) : sortedData.length > 0 ? (
                  sortedData.map((row: any, index: number) => (
                    <tr 
                      key={`${row.id}`} 
                      className={`h-[52px] bg-white hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-200 ${
                        selectedRowIndex === index ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => {
                        onRowSelect?.(selectedRowIndex === index ? null : index);
                      }}
                    >
                      {/* Sticky columns */}
                      {isColumnVisible('sl-no') && (
                        <td className={`sticky ${getStickyLeft('sl-no')} z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>{index + 1}</td>
                      )}
                      {statusCalculationMethod === 'Instantaneous' && isColumnVisible('status') && (
                        <td className={`sticky ${getStickyLeft('status')} z-10 bg-white px-4 text-center text-xs shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>
                          {(() => {
                            const instStatus = row.instantaneous_status;
                            let statusDisplay;
                            
                            if (instStatus !== undefined && instStatus !== null) {
                              statusDisplay = getStatusDisplay(instStatus);
                            } else {
                              const calculatedStatus = getTiltStatus(statusCalculationMethod, row);
                              statusDisplay = getStatusDisplay(calculatedStatus);
                            }
                            
                            return (
                              <div className="flex items-center justify-center">
                                {statusDisplay.icon}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      {isColumnVisible('event') && (
                        <td className={`sticky ${getStickyLeft('event')} z-10 bg-white px-4 text-center text-xs text-gray-600 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}>{row.event || '-'}</td>
                      )}
                      {isColumnVisible('time') && (
                        <td className={`sticky ${getStickyLeft('time')} z-10 bg-white px-4 text-center text-xs text-gray-600`} style={{ boxShadow: '4px 0 6px -2px rgba(0,0,0,0.2)' }}>{formatDateTime(row.time * 1000)}</td>
                      )}
                      
                      {/* Scrollable columns */}
                      {isColumnVisible('tilt') && (
                        <td className="px-4 text-center text-xs text-gray-600">{row.tilt || '0'}</td>
                      )}
                      {isColumnVisible('pass') && (
                        <td className="px-4 text-center text-xs text-gray-600">
                          {(() => {
                            if (row.isM500System && row.m500ZoneDetails) {
                              return row.m500ZoneDetails.PassNum || "";
                            } else {
                              const rawPass = row.pass_name || row.pass || "";
                              if (rawPass) {
                                return rawPass;
                              }
                              try {
                                const zoneData = JSON.parse(row.zone_name || row.zone || "{}");
                                return zoneData.PassNum || "";
                              } catch {
                                return rawPass || "-";
                              }
                            }
                          })()}
                        </td>
                      )}
                      {hasM500Data && isColumnVisible('pipe') && (
                        <td className="px-4 text-center text-xs text-gray-600">
                          {(() => {
                            if (row.isM500System && row.m500ZoneDetails) {
                              return row.m500ZoneDetails.PipeNum || "";
                            } else {
                              return "-";
                            }
                          })()}
                        </td>
                      )}
                      {isColumnVisible('zone') && (
                        <td className="px-4 text-center text-xs text-gray-600">
                          {(() => {
                            if (row.isM500System && row.m500ZoneDetails) {
                              return row.m500ZoneDetails.ZoneNum || "";
                            } else {
                              const rawZone = row.zone_name || row.zone || "";
                              try {
                                const zoneData = JSON.parse(rawZone);
                                return zoneData.ZoneNum || rawZone;
                              } catch {
                                return rawZone || "-";
                              }
                            }
                          })()}
                        </td>
                      )}
                      {isColumnVisible('distance') && (
                        <td className="px-4 text-center text-xs text-gray-600">{renderParameterValue(row.distance, 'distance', (decimalPlaces as any).distance || 2, (row as any).distance_status)}</td>
                      )}
                      
                      {/* Parameter columns */}
                      {isColumnVisible('travel-speed') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.travel_speed, 'travelSpeed', decimalPlaces.travel_speed || 1, row.travel_speed_status)}</td>
                      )}
                      {isColumnVisible('voltage') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.volts, 'voltage', decimalPlaces.volts || 1, row.volts_status)}</td>
                      )}
                      {isColumnVisible('current') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.current, 'current', decimalPlaces.current || 2, row.current_status)}</td>
                      )}
                      {isColumnVisible('wire-speed') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.wire_speed, 'wireSpeed', decimalPlaces.wire_speed || 0, row.wire_speed_status)}</td>
                      )}
                      {isColumnVisible('oscillation-width') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.oscillation_width, 'oscillationWidth', decimalPlaces.oscillation_width || 2, row.oscillation_width_status)}</td>
                      )}
                      {isColumnVisible('target') && (
                        <td className="px-4 text-center text-xs text-gray-600">{row.target?.toFixed(0) || '-'}</td>
                      )}
                      {isColumnVisible('horizontal-bias') && (
                        <td className="px-4 text-center text-xs text-gray-600">{row.horizontal_bias?.toFixed(0) || '-'}</td>
                      )}
                      {isColumnVisible('frequency') && (
                        <td className="px-4 text-center text-xs text-gray-600">{row.frequency?.toFixed(0) || '-'}</td>
                      )}
                      {isColumnVisible('total-wire-consumed') && (
                        <td className="px-4 text-center text-xs text-gray-600">
                          {renderParameterValue((row as any).total_wire_consumed, 'totalWireConsumed', 0, true)}
                        </td>
                      )}
                      {isColumnVisible('true-energy') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.true_energy, 'trueEnergy', decimalPlaces.true_energy || 1, row.true_energy_status)}</td>
                      )}
                      {isColumnVisible('heat') && (
                        <td className="px-4 text-center text-xs">{renderParameterValue(row.heat, 'heat', decimalPlaces.heat || 2, row.heat_status)}</td>
                      )}
                      {hasM500Data && isColumnVisible('head-angle') && (
                        <td className="px-4 text-center text-xs text-gray-600">{row.head_angle?.toFixed(0) || '-'}</td>
                      )}
                      {hasM500Data && isColumnVisible('arc-impedance') && (
                        <td className="px-4 text-center text-xs text-gray-600">{row.arc_impedance?.toFixed(0) || '-'}</td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={visibleColumns ? visibleColumns.length : (hasM500Data ? 21 : 20)} className="px-4 py-8 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
      </div>
    </div>
  );
};

export default TiltViewDetailTable; 